import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  getCanonicalUrl,
  getSeoForPath,
} from '../config/seo';

const JSON_LD_ID = 'mystoryme-json-ld';

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertPropertyMeta = (property, content) => {
  upsertMeta('property', property, content);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const removeJsonLd = () => {
  const existing = document.getElementById(JSON_LD_ID);
  if (existing) existing.remove();
};

const applyJsonLd = (data) => {
  removeJsonLd();
  if (!data) return;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = JSON_LD_ID;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

/**
 * Updates document title and meta tags on route change (SPA SEO).
 * Does not alter page content or business logic.
 */
const SeoHead = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getSeoForPath(pathname);
    const canonical = seo.noCanonical ? null : getCanonicalUrl(pathname);

    document.title = seo.title;

    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'robots', seo.robots);

    if (canonical) {
      upsertLink('canonical', canonical);
    } else {
      const canonicalEl = document.querySelector('link[rel="canonical"]');
      if (canonicalEl) canonicalEl.remove();
    }

    const og = seo.og || {};
    const ogUrl = canonical || og.url;
    upsertPropertyMeta('og:site_name', og.siteName || SITE_NAME);
    upsertPropertyMeta('og:title', og.title || seo.title);
    upsertPropertyMeta('og:description', og.description || seo.description);
    upsertPropertyMeta('og:image', og.image || DEFAULT_OG_IMAGE);
    upsertPropertyMeta('og:image:secure_url', og.image || DEFAULT_OG_IMAGE);
    upsertPropertyMeta('og:image:alt', og.imageAlt);
    upsertPropertyMeta('og:image:width', og.imageWidth || '1200');
    upsertPropertyMeta('og:image:height', og.imageHeight || '630');
    upsertPropertyMeta('og:url', ogUrl);
    upsertPropertyMeta('og:type', og.type || 'website');
    upsertPropertyMeta('og:locale', og.locale || 'en_US');

    const twitter = seo.twitter || {};
    upsertMeta('name', 'twitter:card', twitter.card || 'summary_large_image');
    upsertMeta('name', 'twitter:title', twitter.title || og.title || seo.title);
    upsertMeta(
      'name',
      'twitter:description',
      twitter.description || og.description || seo.description
    );
    upsertMeta('name', 'twitter:image', twitter.image || og.image || DEFAULT_OG_IMAGE);
    upsertMeta(
      'name',
      'twitter:image:alt',
      twitter.imageAlt || og.imageAlt
    );

    applyJsonLd(seo.jsonLd || null);
  }, [pathname]);

  return null;
};

export default SeoHead;
